import React, { useMemo } from "react";
import { gbp } from "../lib/format";
import { PieChart } from "lucide-react";

// Deep-slate + gold family palette
const SECTOR_COLORS = {
  Office: "#0F172A",
  Retail: "#D4AF37",
  Industrial: "#7C6410",
  Mixed: "#64748B",
};

// Sectors always shown in the legend so users can filter into empty states.
const FIXED_SECTORS = ["Office", "Retail", "Industrial"];

const buildSlices = (investments) => {
  const totals = new Map();
  FIXED_SECTORS.forEach((s) => totals.set(s, 0));
  (investments || []).forEach((inv) => {
    const key = inv.property_asset_type || "Mixed";
    totals.set(key, (totals.get(key) || 0) + inv.amount_gbp);
  });
  const total = Array.from(totals.values()).reduce((s, v) => s + v, 0);
  if (total === 0) return { slices: [], total: 0 };
  const slices = Array.from(totals.entries()).map(([label, value]) => ({
    label,
    value,
    pct: (value / total) * 100,
    color: SECTOR_COLORS[label] || "#94A3B8",
  }));
  slices.sort((a, b) => b.value - a.value);
  return { slices, total };
};

export const SectorDonut = ({ investments, activeSector, onSectorClick }) => {
  const { slices, total } = useMemo(() => buildSlices(investments), [investments]);

  const size = 220;
  const stroke = 30;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  const largest = slices[0];
  const clickable = typeof onSectorClick === "function";

  const handleClick = (label) => {
    if (!clickable) return;
    onSectorClick(activeSector === label ? null : label);
  };

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white p-6"
      data-testid="sector-donut"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="overline flex items-center gap-2">
            <PieChart className="h-3 w-3 brickstok-gold" /> Diversification
          </div>
          <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight text-slate-900">
            Sector Allocation
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {clickable ? "Click any sector to filter your assets below." : "Capital deployed by commercial asset class."}
          </p>
        </div>
        {largest && (
          <div className="text-right">
            <div className="overline">Top Sector</div>
            <div className="mt-1 font-display text-lg font-semibold text-slate-900" data-testid="donut-top-sector">
              {largest.label} · {largest.pct.toFixed(0)}%
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col items-center gap-8 md:flex-row md:items-center">
        {total === 0 ? (
          <div className="flex h-[220px] w-full items-center justify-center rounded-md border border-dashed border-slate-200 text-center" data-testid="donut-empty">
            <div>
              <div className="overline">No allocations yet</div>
              <p className="mt-2 max-w-xs text-sm text-slate-500">Your sector breakdown will appear here once you make your first allocation.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="relative" style={{ width: size, height: size }} data-testid="donut-svg">
              <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="transparent"
                  stroke="#F1F5F9"
                  strokeWidth={stroke}
                />
                {slices.map((s) => {
                  if (s.pct <= 0) return null;
                  const dash = (s.pct / 100) * circumference;
                  const isActive = activeSector === s.label;
                  const isDimmed = activeSector && !isActive;
                  const el = (
                    <circle
                      key={s.label}
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      fill="transparent"
                      stroke={s.color}
                      strokeWidth={isActive ? stroke + 6 : stroke}
                      strokeDasharray={`${dash} ${circumference - dash}`}
                      strokeDashoffset={-offset}
                      strokeLinecap="butt"
                      transform={`rotate(-90 ${size / 2} ${size / 2})`}
                      style={{
                        transition: "stroke-dasharray 700ms ease, stroke-width 200ms ease, opacity 200ms ease",
                        opacity: isDimmed ? 0.28 : 1,
                        cursor: clickable ? "pointer" : "default",
                      }}
                      onClick={() => handleClick(s.label)}
                      data-testid={`donut-slice-${s.label}`}
                    />
                  );
                  offset += dash;
                  return el;
                })}
              </svg>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <div className="overline">{activeSector ? "Selected" : "Total Deployed"}</div>
                <div className="mt-1 font-display text-2xl font-semibold text-slate-900 font-mono-num" data-testid="donut-total">
                  {activeSector
                    ? gbp(
                        slices.find((s) => s.label === activeSector)?.value ?? 0,
                        { compact: true }
                      )
                    : gbp(total, { compact: true })}
                </div>
                {activeSector && (
                  <div className="mt-1 text-xs font-semibold uppercase tracking-widest" style={{ color: "#D4AF37" }}>
                    {activeSector}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 space-y-2" data-testid="donut-legend">
              {slices.map((s) => {
                const isActive = activeSector === s.label;
                const isZero = s.pct === 0;
                return (
                  <button
                    key={s.label}
                    onClick={() => handleClick(s.label)}
                    disabled={!clickable}
                    className={`w-full rounded-md border px-3 py-2.5 text-left transition-all duration-200 ${
                      isActive ? "border-[#D4AF37] bg-[rgba(212,175,55,0.08)]" : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                    } ${clickable ? "cursor-pointer" : "cursor-default"} ${isZero ? "opacity-60" : ""}`}
                    data-testid={`donut-legend-${s.label}`}
                    aria-pressed={isActive}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
                        style={{ background: isZero ? "#CBD5E1" : s.color }}
                      />
                      <div className="flex-1">
                        <div className="flex items-baseline justify-between">
                          <span className={`text-sm font-medium ${isZero ? "text-slate-500" : isActive ? "text-slate-900" : "text-slate-800"}`}>
                            {s.label}
                            {isZero && (
                              <span className="ml-2 whitespace-nowrap rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                                No holdings
                              </span>
                            )}
                          </span>
                          <span className={`font-mono-num text-sm font-semibold ${isZero ? "text-slate-500" : "text-slate-900"}`}>{s.pct.toFixed(1)}%</span>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full transition-[width] duration-700"
                            style={{ width: `${s.pct}%`, background: s.color }}
                          />
                        </div>
                        <div className={`mt-1 text-xs font-mono-num ${isZero ? "text-slate-400" : "text-slate-500"}`}>{gbp(s.value)}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default SectorDonut;
